import React from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  PLANS,
  GLOBAL_PLAN_ORDER,
  DURATIONS,
  getAvailableAddOns,
  computePricing,
} from '../settings/ManageCompany/wizardConfig';
import {
  STRIPE as STRIPE_ENDPOINTS,
  ONBOARDING,
} from '../global/Server/endpoints';

/**
 * Public 4-step onboarding wizard (Plan → Company → Payment → Thank You) opened
 * from the landing page CTAs. Anonymous flow — no Bearer token — so it talks to
 * /onboarding/checkout|status and /stripe/publicConfig directly with a bare
 * axios (no AuthUtil, which would auto-logout on a 401 and redirect a public
 * visitor to /login). The plan/feature data is the SAME wizardConfig the
 * internal Create Company wizard uses, so prices/features always match.
 *
 * Payment uses Stripe Elements: /onboarding/checkout returns a clientSecret for
 * a PaymentIntent ({kind:'onboarding'}); the PaymentElement confirms it and the
 * Stripe webhook flips the order to Paid. Step 4 polls /onboarding/status until
 * the webhook lands.
 */

const STEP_LABELS = ['Plan', 'Company', 'Payment', 'Done'];
const STEP_TITLES = [
  'Select a Plan',
  'Company Information',
  'Payment',
  'Thank You',
];

const publicPost = (url, data) =>
  axios.post(url, data, { headers: { 'Content-Type': 'application/json' } });

// Whole dollars show without decimals ($1,200); fee/total show cents ($9.27).
const money = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return '$0';
  const opts = Number.isInteger(n)
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return `$${n.toLocaleString('en-US', opts)}`;
};

const DURATION_BY_ID = DURATIONS.reduce((acc, d) => {
  acc[d.id] = d;
  return acc;
}, {});

// ---- Stripe payment sub-form ------------------------------------------------

function OnboardingPaymentForm({
  total,
  orderNumber,
  paymentIntentId,
  onPaid,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const inFlight = React.useRef(false);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const pollStatus = async () => {
    // Wait for the charge to be confirmed (webhook or active confirm). The
    // CompanyOrder is created server-side only on success, keyed by the
    // PaymentIntent id we pass here — so an abandoned checkout writes nothing.
    // ~24s budget.
    for (let i = 0; i < 20; i += 1) {
      try {
        const res = await publicPost(ONBOARDING.STATUS, {
          orderNumber,
          paymentIntentId,
        });
        if (res?.data?.paid) return true;
      } catch (e) {
        /* keep polling */
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 1200));
    }
    return false;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (inFlight.current || busy) return;
    if (!stripe || !elements) return;

    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });
      if (result?.error) {
        setError(
          result.error.message ||
            (result.error.code === 'card_declined'
              ? 'Your card was declined.'
              : 'Payment failed. Please try again.')
        );
        return;
      }
      // Charge succeeded client-side — wait for the webhook to credit the order.
      const paid = await pollStatus();
      onPaid(paid);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Onboarding] confirmPayment threw:', err);
      setError(
        err?.message ? `Payment failed: ${err.message}` : 'Payment failed.'
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Keep the PaymentElement mounted while confirming (just hidden) so
          stripe.confirmPayment's elements reference stays valid. */}
      <div style={{ display: busy ? 'none' : 'block' }}>
        <PaymentElement options={{ layout: 'tabs' }} />
        {error && <div className="nv-err">{error}</div>}
        <div className="nv-pay-note">
          <span role="img" aria-label="secure">
            🔒
          </span>{' '}
          Secure checkout powered by Stripe.
        </div>
        <div
          className="nv-wiz-foot"
          style={{ borderTop: 'none', padding: '16px 0 0' }}
        >
          <span />
          <button
            type="submit"
            className="nv-btn nv-btn-gold"
            disabled={!stripe || busy}
          >
            {`Pay ${money(total)} & Submit`}
          </button>
        </div>
      </div>

      {busy && (
        <div className="nv-processing">
          <div className="nv-loader" />
          <div className="nv-processing-t">Processing your payment…</div>
          <div className="nv-processing-s">
            This can take a few seconds. Please don&apos;t close or refresh this
            window.
          </div>
        </div>
      )}
    </form>
  );
}

// ---- Wizard -----------------------------------------------------------------

class OnboardingWizard extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.freshState(props.initialPlanId);
  }

  freshState(planId) {
    const validPlan =
      planId && GLOBAL_PLAN_ORDER.includes(planId) ? planId : 'premium';
    return {
      step: 1,
      planId: validPlan,
      addOnIds: [],
      durationId: '1',
      co: {
        company: '',
        owner: '',
        email: '',
        phone: '+1 ',
        address: '',
        contact: 'Phone',
      },
      checkout: null, // { orderNumber, clientSecret, base, fee, total }
      stripePromise: null,
      creating: false,
      error: null,
    };
  }

  // Add-ons available for the selected plan (those the plan doesn't already
  // include), with any stale selections pruned out.
  availableAddOns() {
    return getAvailableAddOns(this.state.planId);
  }

  selectedAddOns() {
    const avail = new Set(this.availableAddOns().map((a) => a.id));
    return this.state.addOnIds.filter((id) => avail.has(id));
  }

  pricing() {
    return computePricing({
      planId: this.state.planId,
      addOnIds: this.selectedAddOns(),
      durationId: this.state.durationId,
    });
  }

  toggleAddOn = (id) =>
    this.setState((s) => ({
      addOnIds: s.addOnIds.includes(id)
        ? s.addOnIds.filter((x) => x !== id)
        : [...s.addOnIds, id],
    }));

  setDuration = (durationId) => this.setState({ durationId });

  componentDidUpdate(prevProps) {
    // Re-seed when the dialog is (re)opened, honoring a pre-selected plan.
    if (!prevProps.open && this.props.open) {
      this._checkoutStarted = false;
      this.setState(this.freshState(this.props.initialPlanId));
    }
  }

  plan() {
    return PLANS[this.state.planId];
  }

  setPlan = (planId) => this.setState({ planId });

  setCo = (key, value) =>
    this.setState((s) => ({ co: { ...s.co, [key]: value } }));

  // Live-format a US phone as "+1 (XXX) XXX-XXXX" (matches the field hint).
  // The "+1 " country code is always kept; a leading "1" the user types is
  // treated as that code, not part of the number (US area codes never start
  // with 1).
  formatPhone = (raw) => {
    let d = (raw || '').replace(/\D/g, '');
    if (d.startsWith('1')) d = d.slice(1);
    d = d.slice(0, 10);
    if (!d) return '+1 ';
    let out = '+1 (' + d.slice(0, 3);
    if (d.length >= 3) out += ')';
    if (d.length > 3) out += ' ' + d.slice(3, 6);
    if (d.length > 6) out += '-' + d.slice(6, 10);
    return out;
  };

  goTo = (step) => this.setState({ step });

  // Step 2 → 3: validate required fields then create the order + PaymentIntent.
  continueToPayment = async () => {
    const { co } = this.state;
    const phoneDigits = (co.phone || '').replace(/\D/g, '').replace(/^1/, '');
    if (!co.company || !co.owner || !co.email || !phoneDigits) {
      this.setState({
        error:
          'Please complete Company Name, Owner Name, Email and Phone to continue.',
      });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(co.email)) {
      this.setState({ error: 'Please enter a valid email address.' });
      return;
    }
    if (phoneDigits.length !== 10) {
      this.setState({ error: 'Please enter a valid 10-digit phone number.' });
      return;
    }
    this.setState({ error: null, step: 3 });
    this.startCheckout();
  };

  startCheckout = async () => {
    // Synchronous guard (instance flag, not async state) so a double-click or
    // re-render can NEVER create two orders / PaymentIntents — exactly one
    // checkout request per session.
    if (this.state.checkout || this._checkoutStarted) return;
    this._checkoutStarted = true;
    this.setState({ creating: true, error: null });
    try {
      // Load Stripe (publishable key) and create the order in parallel.
      const cfgPromise = axios.get(STRIPE_ENDPOINTS.PUBLIC_CONFIG);
      const plan = this.plan();
      const checkoutPromise = publicPost(ONBOARDING.CHECKOUT, {
        ...this.state.co,
        planId: this.state.planId,
        plan: plan.name,
        addOns: this.selectedAddOns(),
        durationId: this.state.durationId,
      });

      const cfg = await cfgPromise;
      if (!cfg?.data?.success || !cfg.data.publishableKey) {
        throw new Error(
          cfg?.data?.message || 'Payment is not available right now.'
        );
      }
      const checkoutRes = await checkoutPromise;
      if (!checkoutRes?.data?.success) {
        throw new Error(
          checkoutRes?.data?.message || 'Could not start checkout.'
        );
      }
      const d = checkoutRes.data;
      this.setState({
        stripePromise: loadStripe(cfg.data.publishableKey),
        checkout: {
          orderNumber: d.orderNumber,
          paymentIntentId: d.paymentIntentId,
          clientSecret: d.clientSecret,
          base: d.baseUSD != null ? d.baseUSD : d.amount,
          fee: d.feeUSD,
          total: d.totalUSD != null ? d.totalUSD : d.amount,
        },
        creating: false,
      });
    } catch (err) {
      // Allow a retry after a failure.
      this._checkoutStarted = false;
      this.setState({
        creating: false,
        error: err?.message || 'Could not start checkout. Please try again.',
      });
    }
  };

  onPaid = () => {
    // The webhook may lag; we advance to Thank You regardless and show the
    // order. Status there reflects PAID / PENDING SETUP per spec.
    this.setState({ step: 4 });
  };

  render() {
    if (!this.props.open) return null;
    const { step, planId, co, checkout, stripePromise, creating, error } =
      this.state;
    const plan = this.plan();
    const pr = this.pricing();
    const addOnObjs = this.availableAddOns().filter((a) =>
      this.selectedAddOns().includes(a.id)
    );
    const duration = DURATION_BY_ID[this.state.durationId] || DURATIONS[0];
    // fee = Stripe processing fee; totalDue = charged total (incl. fee). Until the
    // checkout call returns, fall back to the client-computed term total.
    const fee = checkout ? checkout.fee : null;
    const totalDue =
      checkout && checkout.total != null ? checkout.total : pr.total;

    return (
      <div className="nv-landing nv-wiz-page">
        <div className="nv-wiz">
          <div className="nv-wiz-bar">
            <button
              className="nv-wiz-back"
              aria-label="Back"
              onClick={() =>
                step > 1 && step < 4
                  ? this.goTo(step - 1)
                  : this.props.onClose()
              }
            >
              ←
            </button>
            <span className="nv-t">Get Started</span>
            <button
              className="nv-wiz-x"
              aria-label="Back to home"
              onClick={this.props.onClose}
            >
              ×
            </button>
          </div>

          {/* progress */}
          <div className="nv-wiz-prog">
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              const cls = n === step ? 'nv-on' : n < step ? 'nv-done' : '';
              return (
                <div className={`nv-wp ${cls}`} key={label}>
                  <div className="nv-c">{n < step ? '✓' : n}</div>
                  {label}
                </div>
              );
            })}
          </div>

          <div className="nv-wiz-head">
            <span className="nv-stp">
              {step < 4 ? `STEP ${step} OF 4` : 'CONFIRMED'}
            </span>
            <h3>{STEP_TITLES[step - 1]}</h3>
          </div>

          <div className="nv-wiz-body">
            {/* STEP 1 — PLAN */}
            {step === 1 && (
              <>
                <div className="nv-free-banner">
                  <span
                    className="nv-free-em"
                    role="img"
                    aria-label="celebrate"
                  >
                    🎉
                  </span>
                  <div>
                    <b>1 month free on all plans.</b> Your first payment is
                    collected now to activate your account.
                  </div>
                </div>
                <p
                  style={{
                    color: '#5B6781',
                    fontSize: 13.5,
                    margin: '-2px 0 14px',
                  }}
                >
                  Choose the plan that fits your operation. You can upgrade
                  anytime.
                </p>
                {GLOBAL_PLAN_ORDER.map((pid) => {
                  const p = PLANS[pid];
                  return (
                    <div
                      key={pid}
                      className={`nv-wplan ${planId === pid ? 'nv-sel' : ''}`}
                      style={{ '--plan-color': p.color }}
                      onClick={() => this.setPlan(pid)}
                    >
                      {p.badge && <span className="nv-pop2">{p.badge}</span>}
                      <div className="nv-pn">{p.name}</div>
                      <div className="nv-pp">
                        ${p.price}
                        <small>/mo</small>
                      </div>
                      <div className="nv-pd">{planTagline(pid)}</div>
                    </div>
                  );
                })}

                {/* ADD-ONS */}
                {this.availableAddOns().length > 0 && (
                  <div style={{ marginTop: 22 }}>
                    <div style={SS_LABEL}>Add-ons (optional)</div>
                    {this.availableAddOns().map((a) => {
                      const on = this.selectedAddOns().includes(a.id);
                      return (
                        <div
                          key={a.id}
                          onClick={() => this.toggleAddOn(a.id)}
                          style={addonRow(on)}
                        >
                          <span style={checkbox(on)}>{on ? '✓' : ''}</span>
                          <span
                            style={{
                              flex: 1,
                              fontWeight: 600,
                              color: '#0A1640',
                            }}
                          >
                            {a.name}
                          </span>
                          <span style={{ fontWeight: 700, color: '#1E3FD0' }}>
                            +${a.price}/mo
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* LICENSE DURATION */}
                <div style={{ marginTop: 22 }}>
                  <div style={SS_LABEL}>License Duration</div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 10,
                    }}
                  >
                    {DURATIONS.map((d) => {
                      const on = this.state.durationId === d.id;
                      return (
                        <div
                          key={d.id}
                          onClick={() => this.setDuration(d.id)}
                          style={durCard(on)}
                        >
                          <div style={{ fontWeight: 700, color: '#0A1640' }}>
                            {d.label}
                          </div>
                          {d.badge && (
                            <span style={durBadge(d.badgeColor || '#5b6b8a')}>
                              {d.badge}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* COMPACT PRICE PREVIEW */}
                <div style={PREVIEW_BOX}>
                  <div style={PREVIEW_ROW}>
                    <span>Monthly</span>
                    <b>{money(pr.monthly)}/mo</b>
                  </div>
                  <div style={PREVIEW_ROW}>
                    <span>Term</span>
                    <b>{duration.label}</b>
                  </div>
                  {pr.discount > 0 && (
                    <div style={PREVIEW_ROW}>
                      <span>Term discount</span>
                      <b style={{ color: '#16A34A' }}>−{money(pr.discount)}</b>
                    </div>
                  )}
                  <div style={PREVIEW_TOTAL}>
                    <span>Due today</span>
                    <span>{money(pr.total)}</span>
                  </div>
                  <div style={FREE_NOTE}>
                    <span role="img" aria-label="gift">
                      🎁
                    </span>{' '}
                    <span>
                      <b>Your first month is free.</b> This payment is collected
                      now and credited to your first billing cycle.
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 11.5, color: '#9aa6bd', marginTop: 6 }}
                  >
                    A small card processing fee is added at payment.
                  </div>
                </div>
              </>
            )}

            {/* STEP 2 — COMPANY */}
            {step === 2 && (
              <>
                <div className="nv-wlabel">
                  Company Name <i>*</i>
                </div>
                <input
                  className="nv-winput"
                  value={co.company}
                  placeholder="e.g. FUEGO 777"
                  onChange={(e) => this.setCo('company', e.target.value)}
                />
                <div className="nv-wlabel">
                  Owner Name <i>*</i>
                </div>
                <input
                  className="nv-winput"
                  value={co.owner}
                  placeholder="Full name"
                  onChange={(e) => this.setCo('owner', e.target.value)}
                />
                <div className="nv-wrow">
                  <div>
                    <div className="nv-wlabel">
                      Email <i>*</i>
                    </div>
                    <input
                      className="nv-winput"
                      value={co.email}
                      placeholder="name@email.com"
                      onChange={(e) => this.setCo('email', e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="nv-wlabel">
                      Phone <i>*</i>
                    </div>
                    <input
                      className="nv-winput"
                      value={co.phone}
                      placeholder="+1 (000) 000-0000"
                      inputMode="tel"
                      onChange={(e) =>
                        this.setCo('phone', this.formatPhone(e.target.value))
                      }
                    />
                  </div>
                </div>
                <div className="nv-wlabel">Business Address</div>
                <input
                  className="nv-winput"
                  value={co.address}
                  placeholder="Street, City, State"
                  onChange={(e) => this.setCo('address', e.target.value)}
                />
                <div className="nv-wlabel">Preferred Contact</div>
                <select
                  className="nv-winput"
                  value={co.contact}
                  onChange={(e) => this.setCo('contact', e.target.value)}
                >
                  {['Phone', 'Email', 'Text Message'].map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
                {error && <div className="nv-err">{error}</div>}
              </>
            )}

            {/* STEP 3 — PAYMENT */}
            {step === 3 && (
              <>
                <div className="nv-free-banner">
                  <span
                    className="nv-free-em"
                    role="img"
                    aria-label="celebrate"
                  >
                    🎉
                  </span>
                  <div>
                    <b>First month free.</b> We collect your first payment now
                    to activate your account.
                  </div>
                </div>
                <div className="nv-osum">
                  <div className="nv-l">
                    <span>{plan.name} Plan (monthly)</span>
                    <b>{money(plan.price)}</b>
                  </div>
                  {addOnObjs.map((a) => (
                    <div className="nv-l" key={a.id}>
                      <span>+ {a.name} (monthly)</span>
                      <b>{money(a.price)}</b>
                    </div>
                  ))}
                  {pr.months > 1 && (
                    <div className="nv-l">
                      <span>{pr.months} months</span>
                      <b>{money(pr.gross)}</b>
                    </div>
                  )}
                  {pr.discount > 0 && (
                    <div className="nv-l">
                      <span>Term discount</span>
                      <b style={{ color: '#16A34A' }}>−{money(pr.discount)}</b>
                    </div>
                  )}
                  {fee != null && Number(fee) > 0 && (
                    <div className="nv-l">
                      <span>Fees</span>
                      <b>{money(fee)}</b>
                    </div>
                  )}
                  <div className="nv-tot">
                    <span>Total Due Today</span>
                    <span>{money(totalDue)}</span>
                  </div>
                </div>
                {creating && (
                  <div className="nv-spin">Preparing secure checkout…</div>
                )}
                {error && <div className="nv-err">{error}</div>}
                {!creating && checkout && stripePromise && (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret: checkout.clientSecret,
                      appearance: { theme: 'stripe' },
                    }}
                  >
                    <OnboardingPaymentForm
                      total={totalDue}
                      orderNumber={checkout.orderNumber}
                      paymentIntentId={checkout.paymentIntentId}
                      onPaid={this.onPaid}
                    />
                  </Elements>
                )}
                {!creating && error && (
                  <button
                    className="nv-btn nv-btn-ghost-d"
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      this.setState({ checkout: null });
                      this.startCheckout();
                    }}
                  >
                    Retry
                  </button>
                )}
              </>
            )}

            {/* STEP 4 — THANK YOU */}
            {step === 4 && (
              <div className="nv-othx">
                <span className="nv-em" role="img" aria-label="celebrate">
                  🎉
                </span>
                <h3>Thank you for choosing NuVe System</h3>
                <p>
                  Your order has been received. A receipt has been emailed to{' '}
                  <b>{co.email || 'your email'}</b>.
                </p>
                <div className="nv-ord">
                  <div className="nv-r">
                    <span>Order #</span>
                    <b>{checkout?.orderNumber || '—'}</b>
                  </div>
                  <div className="nv-r">
                    <span>Plan</span>
                    <b>
                      {plan.name} — {money(pr.monthly)}/mo
                    </b>
                  </div>
                  {addOnObjs.length > 0 && (
                    <div className="nv-r">
                      <span>Add-ons</span>
                      <b>{addOnObjs.map((a) => a.name).join(', ')}</b>
                    </div>
                  )}
                  <div className="nv-r">
                    <span>Term</span>
                    <b>{duration.label}</b>
                  </div>
                  {fee != null && Number(fee) > 0 && (
                    <div className="nv-r">
                      <span>Fees</span>
                      <b>{money(fee)}</b>
                    </div>
                  )}
                  <div className="nv-r">
                    <span>Total Paid</span>
                    <b>{money(totalDue)}</b>
                  </div>
                  <div className="nv-r">
                    <span>Payment Status</span>
                    <span className="nv-paid">PAID</span>
                  </div>
                  <div className="nv-r">
                    <span>Company Status</span>
                    <span className="nv-pend">PENDING SETUP</span>
                  </div>
                </div>
                <div className="nv-next">
                  <b>What happens next?</b>
                  <br />
                  Our technical team is now preparing your company account. A
                  technician will review your order, configure your company,
                  create your <b>Master Admin</b> account, and contact you to
                  schedule your onboarding and training session.
                  <div className="nv-eta">
                    <span role="img" aria-label="time">
                      ⏱
                    </span>{' '}
                    Estimated setup time: 24–48 hours
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* footer nav (steps 1, 2, 4 — step 3's button lives in the form) */}
          {step !== 3 && (
            <div className="nv-wiz-foot">
              {step === 1 && (
                <>
                  <button
                    className="nv-btn nv-btn-ghost-d"
                    onClick={this.props.onClose}
                  >
                    Cancel
                  </button>
                  <button
                    className="nv-btn nv-btn-gold"
                    onClick={() => this.goTo(2)}
                  >
                    Continue
                  </button>
                </>
              )}
              {step === 2 && (
                <>
                  <button
                    className="nv-btn nv-btn-ghost-d"
                    onClick={() => this.goTo(1)}
                  >
                    Back
                  </button>
                  <button
                    className="nv-btn nv-btn-gold"
                    onClick={this.continueToPayment}
                  >
                    Continue
                  </button>
                </>
              )}
              {step === 4 && (
                <>
                  <span />
                  <button
                    className="nv-btn nv-btn-gold"
                    onClick={this.props.onClose}
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

// ---- inline style helpers for the new add-on / duration / preview blocks ----

const SS_LABEL = {
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#9aa6bd',
  marginBottom: 10,
};

const addonRow = (on) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '11px 14px',
  borderRadius: 11,
  marginBottom: 8,
  cursor: 'pointer',
  fontSize: 14,
  background: on ? '#EEF3FF' : '#fff',
  border: `1.5px solid ${on ? '#1E3FD0' : '#E5EAF4'}`,
});

const checkbox = (on) => ({
  width: 20,
  height: 20,
  borderRadius: 6,
  display: 'grid',
  placeItems: 'center',
  fontSize: 13,
  color: '#fff',
  flexShrink: 0,
  background: on ? '#1E3FD0' : 'transparent',
  border: `2px solid ${on ? '#1E3FD0' : '#c5cdda'}`,
});

const durCard = (on) => ({
  position: 'relative',
  padding: '14px 14px',
  borderRadius: 11,
  cursor: 'pointer',
  textAlign: 'center',
  background: on ? '#EEF3FF' : '#fff',
  border: `1.5px solid ${on ? '#1E3FD0' : '#E5EAF4'}`,
});

const durBadge = (color) => ({
  display: 'inline-block',
  marginTop: 6,
  padding: '2px 8px',
  borderRadius: 20,
  fontSize: 10.5,
  fontWeight: 800,
  color: '#fff',
  background: color,
});

const PREVIEW_BOX = {
  marginTop: 22,
  background: '#F4F6FB',
  border: '1px solid #E5EAF4',
  borderRadius: 12,
  padding: '14px 16px',
};

const PREVIEW_ROW = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 13.5,
  color: '#5B6781',
  padding: '4px 0',
};

const PREVIEW_TOTAL = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 16,
  fontWeight: 800,
  color: '#0A1640',
  borderTop: '1px solid #E5EAF4',
  marginTop: 8,
  paddingTop: 10,
};

const FREE_NOTE = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  marginTop: 10,
  padding: '9px 11px',
  borderRadius: 9,
  background: '#E9F9F0',
  border: '1px solid #BBE9CE',
  color: '#15803D',
  fontSize: 12.5,
  lineHeight: 1.45,
};

function planTagline(pid) {
  switch (pid) {
    case 'standard':
      return 'Core tools to run a single floor — memberships, employees & reporting.';
    case 'premium':
      return 'Full control: fraud alerts, payouts, multi-location & analytics.';
    case 'pro_elite':
      return 'Everything in Premium plus advanced accounting & automation.';
    default:
      return '';
  }
}

export default OnboardingWizard;
